import type { IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import type { Classification } from '../classification/ClassificationService';
import { describeOutputBadge, outputIcon } from './PolicyDerivation';
import { formatVerdict, isConcerningValue, verdictTone, type ClassifiedItem } from './PolicyVerdict';
import type { PolicyStatus, PolicyView } from './PolicyTypes';

/** Pull enough distinct verdicts to catch rare-but-concerning values (display is sliced smaller). */
const DISTRIBUTION_LIMIT = 50;
/** A material share of concerning verdicts escalates a policy from "flagged" to "critical". */
const CRITICAL_SHARE = 0.05;

function policyStatus(turns: number, concernTurns: number): PolicyStatus {
    if (turns === 0) return 'empty';
    if (concernTurns === 0) return 'clear';
    return concernTurns / turns >= CRITICAL_SHARE ? 'critical' : 'flagged';
}
/** Max raw turns pulled for a single policy's detail table. */
export const RECORD_LIMIT = 250;
/** Turns scanned for the cross-policy flagged-activity feed (filtered to concerning verdicts). */
export const ACTIVITY_SCAN_LIMIT = 1000;

/** The OLAP dimensions key for a policy's verdict, or null when the policy has no field. */
export function policyFieldName(policy: Classification): string | null {
    return policy.fieldName !== undefined && policy.fieldName.length > 0 ? policy.fieldName : null;
}

/**
 * Loads one policy's real classified activity over the active date range: verdict volume, value
 * distribution, mean confidence, and a weekly volume trend for the sparkline. A policy
 * with no field (or no classified turns) returns zeroed activity, not fabricated data.
 */
export async function loadPolicyView(gateway: IAnalyticsGateway, policy: Classification): Promise<PolicyView> {
    const fieldName = policyFieldName(policy);
    const base: PolicyView = {
        policy,
        fieldName,
        icon: outputIcon(policy.outputType),
        outputBadge: describeOutputBadge(policy),
        turns: 0,
        status: 'empty',
        concernTurns: 0,
        topValue: null,
        distribution: [],
        avgConfidence: null,
        spark: [],
    };
    if (fieldName === null) return base;

    const [agg, trend] = await Promise.all([
        gateway.aggregate({
            source: 'interactions',
            groupBy: [fieldName],
            metrics: ['turns', 'avgConfidence'],
            presentDimensions: [fieldName],
            orderBy: { metric: 'turns', direction: 'desc' },
            limit: DISTRIBUTION_LIMIT,
        }),
        gateway.trend({
            source: 'interactions',
            bucket: 'day',
            metrics: ['turns'],
            presentDimensions: [fieldName],
        }),
    ]);

    const distribution = agg.rows.map((row) => {
        const value = row.keys[fieldName] ?? '—';
        return { value, count: row.metrics.turns, concern: isConcerningValue(policy.outputType, value, policy.outputConfig) };
    });
    const turns = agg.totals.turns;
    const concernTurns = distribution.reduce((sum, entry) => (entry.concern ? sum + entry.count : sum), 0);
    return {
        ...base,
        turns,
        status: policyStatus(turns, concernTurns),
        concernTurns,
        topValue: distribution[0]?.value ?? null,
        distribution,
        avgConfidence: turns > 0 ? agg.totals.avgConfidence : null,
        spark: trend.points.map((point) => point.metrics.turns),
    };
}

/** Maps an OLAP interaction record to a classified item for one policy's verdict. */
export function toClassifiedItem(record: InteractionRecord, policy: Classification, fieldName: string): ClassifiedItem {
    const dim = record.dimensions[fieldName];
    const value = dim?.value ?? '—';
    const valueNum = dim?.valueNum ?? null;
    const concern = isConcerningValue(policy.outputType, value, policy.outputConfig);
    return {
        id: record.id,
        sessionId: record.sessionId,
        turnIndex: record.turnIndex,
        timestampIso: record.timestamp,
        person: record.userId.length > 0 ? record.userId : '—',
        department: record.dimensions['user_department']?.value ?? '—',
        output: formatVerdict(policy.outputType, value, valueNum, policy.outputConfig),
        valueNum,
        tone: concern ? 'danger' : verdictTone(policy.outputType, valueNum, policy.outputConfig),
        confidence: dim?.confidence ?? null,
    };
}
